import { useState } from "react";
import { Plus, Ticket, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAdminPartnerCodes } from "@/hooks/useAdminPartnerCodes";
import PartnerCodeForm from "./PartnerCodeForm";
import DeletePartnerCodeDialog from "./DeletePartnerCodeDialog";
import { PartnerCodesStatsCard } from "./partner-codes/PartnerCodesStatsCard";
import { PartnerCodesColumnFilters } from "./partner-codes/PartnerCodesColumnFilters";
import { PartnerCodesTable } from "./partner-codes/PartnerCodesTable";
import type { Tables } from "@/integrations/supabase/types";

type PartnerCode = Tables<"partner_codes">;

const ITEMS_PER_PAGE = 20;

const PartnerCodesList = () => {
  const {
    partnerCodes,
    totalCount,
    totalPages,
    isLoading,
    filterTags,
    addFilter,
    removeFilter,
    clearAllFilters,
    sortField,
    sortDirection,
    handleSort,
    currentPage,
    setCurrentPage,
    showSystemGenerated,
    setShowSystemGenerated,
    systemHiddenCount,
  } = useAdminPartnerCodes();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingCode, setEditingCode] = useState<PartnerCode | null>(null);
  const [deletingCode, setDeletingCode] = useState<PartnerCode | null>(null);

  return (
    <div className="space-y-6">
      {/* Stats Overview Card with Charts */}
      <PartnerCodesStatsCard />

      {/* Partner Codes Table Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Ticket className="h-5 w-5 text-secondary" />
                Partner Codes Management
              </CardTitle>
              <CardDescription>
                View and manage partner discount codes
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Switch
                  id="show-system-codes"
                  checked={showSystemGenerated}
                  onCheckedChange={setShowSystemGenerated}
                />
                <Label htmlFor="show-system-codes" className="text-xs cursor-pointer whitespace-nowrap">
                  Show system-generated
                  {!showSystemGenerated && systemHiddenCount > 0 && (
                    <span className="text-muted-foreground ml-1">({systemHiddenCount} hidden)</span>
                  )}
                </Label>
              </div>
              <PartnerCodesColumnFilters
                filterTags={filterTags}
                addFilter={addFilter}
                removeFilter={removeFilter}
                clearAllFilters={clearAllFilters}
              />
              <Button
                onClick={() => setShowCreateDialog(true)}
                variant="secondary"
                size="sm"
                className="h-8 gap-1.5"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Code
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border overflow-x-auto">
            <PartnerCodesTable
              codes={partnerCodes}
              isLoading={isLoading}
              isEmpty={!isLoading && partnerCodes.length === 0}
              sortField={sortField}
              sortDirection={sortDirection}
              onSort={handleSort}
              onEdit={setEditingCode}
              onDelete={setDeletingCode}
            />
          </div>

          {totalCount > 0 && (
            <div className="flex items-center justify-between mt-3">
              <p className="text-sm text-muted-foreground">
                {totalPages > 1 
                  ? `Showing ${(currentPage - 1) * ITEMS_PER_PAGE + 1} to ${Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of ${totalCount} codes`
                  : `Showing ${totalCount} codes of ${totalCount}`
                }
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <PartnerCodeForm open={showCreateDialog} onOpenChange={setShowCreateDialog} mode="create" />

      {editingCode && (
        <PartnerCodeForm
          open={!!editingCode}
          onOpenChange={(open) => !open && setEditingCode(null)}
          mode="edit"
          partnerCode={editingCode}
        />
      )}

      {deletingCode && (
        <DeletePartnerCodeDialog
          open={!!deletingCode}
          onOpenChange={(open) => !open && setDeletingCode(null)}
          partnerCode={deletingCode}
        />
      )}
    </div>
  );
};

export default PartnerCodesList;
